import React, { useMemo, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  RowSelectionState,
} from '@tanstack/react-table';

// Column sizing metadata interface
export interface ColumnSizingMeta {
  minWidth?: string;
  width?: string;
  maxWidth?: string;
  align?: 'left' | 'right' | 'center';
}

export interface TableProps<T extends object> {
  data: T[];
  columns: ColumnDef<T>[];
  className?: string;
  onRowClick?: (row: T) => void;
  // Row selection props
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  getRowId?: (row: T) => string;
  /** Header row sticks to the top of the nearest scroll container (the ListPage shell). */
  stickyHeader?: boolean;
}

// Indeterminate checkbox component - handles the indeterminate state via ref
interface IndeterminateCheckboxProps {
  indeterminate?: boolean;
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

const IndeterminateCheckbox: React.FC<IndeterminateCheckboxProps> = ({
  indeterminate,
  checked,
  onChange,
  onClick,
  disabled,
  className,
  'aria-label': ariaLabel,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate ?? false;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={className}
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
};

const Table = <T extends object>({ 
  data, 
  columns, 
  className = '', 
  onRowClick,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
  getRowId,
  stickyHeader = false
}: TableProps<T>) => {
  
  // Create checkbox column for row selection
  const selectionColumn: ColumnDef<T> = useMemo(() => ({
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        className="row-select-checkbox"
        checked={table.getIsAllPageRowsSelected()}
        indeterminate={table.getIsSomePageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        aria-label="Select all rows on this page"
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        className="row-select-checkbox"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        aria-label="Select row"
      />
    ),
    meta: {
      minWidth: '40px',
      width: '40px',
      maxWidth: '40px',
      align: 'center' as const,
    },
    enableSorting: false,
  }), []);

  // Combine selection column with provided columns if row selection is enabled
  const allColumns = useMemo(() => {
    if (enableRowSelection) {
      return [selectionColumn, ...columns];
    }
    return columns;
  }, [enableRowSelection, selectionColumn, columns]);

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: enableRowSelection,
    state: enableRowSelection ? { rowSelection } : undefined,
    onRowSelectionChange: enableRowSelection 
      ? (updater) => {
          const newSelection = typeof updater === 'function' 
            ? updater(rowSelection) 
            : updater;
          onRowSelectionChange?.(newSelection);
        }
      : undefined,
    getRowId: getRowId,
  });

  // Helper function to get column styles
  const getColumnStyles = (column: any): React.CSSProperties => {
    const meta = column.columnDef.meta as ColumnSizingMeta | undefined;
    if (!meta) return {};
    
    const styles: React.CSSProperties = {
      minWidth: meta.minWidth,
      width: meta.width,
      maxWidth: meta.maxWidth,
    };
    
    // Only apply textAlign if explicitly set (let CSS handle default left alignment)
    if (meta.align) {
      styles.textAlign = meta.align;
    }
    
    return styles;
  };

  return (
    <div className={`table-container ${className}`}>
      <table className={`table ${stickyHeader ? 'table--sticky-header' : ''}`.trim()}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isSelectColumn = header.id === 'select';
                return (
                  <th
                    key={header.id}
                    className={`table-header ${isSelectColumn ? 'select-column' : ''}`}
                    onClick={isSelectColumn ? undefined : header.column.getToggleSortingHandler()}
                    style={getColumnStyles(header.column)}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {!isSelectColumn && (
                      <span className="sort-indicator">
                        {{
                          asc: ' ↑',
                          desc: ' ↓',
                        }[header.column.getIsSorted() as string] ?? ''}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => {
            const isSelected = enableRowSelection && row.getIsSelected();
            return (
              <tr 
                key={row.id} 
                className={`table-row ${isSelected ? 'selected' : ''}`}
                onClick={() => onRowClick?.(row.original)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {row.getVisibleCells().map(cell => {
                  const isSelectColumn = cell.column.id === 'select';
                  return (
                    <td 
                      key={cell.id} 
                      className={`table-cell ${isSelectColumn ? 'select-column' : ''}`}
                      style={getColumnStyles(cell.column)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
